/**
 * Rounds a weight value based on the courier's rounding mode setting.
 *
 * @param weight - The weight value to round
 * @param roundingMode - 'up' for ceiling, 'down' for floor. Defaults to 'up'
 * @returns The rounded weight value, or the original if undefined/null
 */
export const formatWeight = (
  weight: number | undefined | null,
  roundingMode: 'up' | 'down' = 'up'
): number | null => {
  if (weight === undefined || weight === null) {
    return null;
  }

  if (roundingMode === 'down') {
    return Math.floor(weight);
  }

  // Default to rounding up (ceiling)
  return Math.ceil(weight);
};

/**
 * Formats a weight value as a display string with the appropriate rounding.
 *
 * @param weight - The weight value to format
 * @param roundingMode - 'up' for ceiling, 'down' for floor. Defaults to 'up'
 * @param includeUnit - Whether to include "lbs" suffix. Defaults to true.
 * @returns A formatted string like "5 lbs", "5", or "N/A" if no weight
 */
export const formatWeightDisplay = (
  weight: number | undefined | null,
  roundingMode: 'up' | 'down' = 'up',
  includeUnit: boolean = true
): string => {
  const roundedWeight = formatWeight(weight, roundingMode);

  if (roundedWeight === null) {
    return 'N/A';
  }

  return includeUnit ? `${roundedWeight} lbs` : `${roundedWeight}`;
};
